<template>
    <div>
        <b-modal
            id="itemModal"
            ref="itemModal"
            :lazy="true"
            title-tag="h2"
            title="New Item"
        >
            <div v-if="message" class="alert alert-warning" role="alert">
                <i class="cus-error"></i>
                {{ message }}
            </div>

            <b-form-group label="Name:" class label-cols="4" label-for="name">
                <b-input
                    placeholder="Item Name (required)"
                    v-model="data.name"
                    name="name"
                    v-validate="'required'"
                    :class="{
                        'border-danger': errors.has('name')
                    }"
                />
            </b-form-group>

            <b-form-group
                label="Category:"
                class
                label-cols="4"
                label-for="category"
            >
                <v-select
                    id="category"
                    :options="groupedCategories"
                    v-model="data.category"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('category') }"
                    name="category"
                    label="name"
                    placeholder="Select category (required)"
                    :selectable="category => !category.unselectable"
                />
            </b-form-group>

            <b-form-group
                label="Next Due:"
                class
                label-cols="4"
                label-for="due"
            >
                <b-select
                    class="form-control"
                    v-model="data.due"
                    :options="dueOptions"
                    name="due"
                    :disabled="disable.due"
                ></b-select>
            </b-form-group>

            <b-form-group
                label="Controlled:"
                class
                label-cols="4"
                label-for="control"
            >
                <b-select
                    v-model="data.control"
                    class="form-control"
                    :options="[
                        { text: 'Yes', value: 1 },
                        { text: 'No', value: 0 }
                    ]"
                    name="control"
                    :disabled="disable.controlled"
                ></b-select>
            </b-form-group>

            <b-form-group
                label="Fees:"
                class
                label-cols="4"
                label-for="fees_id"
            >
                <v-select
                    :options="fees"
                    v-model="data.fees_id"
                    name="fees_id"
                    label="display_name"
                    :multiple="true"
                    :reduce="fee => fee.id"
                    placeholder="Fees (optional)"
                    :disabled="disable.fees"
                />
            </b-form-group>

            <b-form-group
                label="Print Label:"
                label-cols="4"
                label-for="print_label"
            >
                <b-select
                    v-model="data.print_label"
                    class="form-control"
                    :options="[
                        { text: 'Yes', value: 1 },
                        { text: 'No', value: 0 }
                    ]"
                    name="print_label"
                ></b-select>
            </b-form-group>

            <b-form-group
                label="Print on Invoice:"
                class
                label-cols="4"
                label-for="is_print_on_invoice"
            >
                <b-select
                    v-model="data.is_print_on_invoice"
                    class="form-control"
                    :options="[
                        { text: 'Yes', value: 1 },
                        { text: 'No', value: 0 }
                    ]"
                    name="is_print_on_invoice"
                ></b-select>
            </b-form-group>

            <b-form-group
                label="Base Price:"
                class
                label-cols="4"
                label-for="price"
            >
                <b-row class="mx-0">
                    <b-input
                        placeholder="Unit Price (Optional)"
                        v-model="data.price"
                        v-validate="''"
                        name="price"
                        type="number"
                        step="0.01"
                        class="col-3 mr-1"
                        :class="{
                            'border-danger': errors.has('price')
                        }"
                    />
                    <b-select
                        v-model="data.cost_type"
                        class="col form-control"
                        :options="COST_TYPE"
                        name="cost_type"
                    ></b-select>
                </b-row>
            </b-form-group>

            <b-form-group
                label="Minimum Price:"
                class
                label-cols="4"
                label-for="min_price"
            >
                <b-row class="mx-0">
                    <b-input
                        placeholder="Min Price (optional)"
                        v-model="data.min_price"
                        name="name"
                        class="col-3 mr-1"
                        :class="{
                            'border-danger': errors.has('min_price')
                        }"
                        :disabled="disable.minPrice"
                    />
                    <span class="ml-1 mt-1"> Fixed </span>
                </b-row>
            </b-form-group>

            <b-form-group
                label="Base Quantity:"
                class
                label-cols="4"
                label-for="dose"
            >
                <b-row class="mx-0">
                    <b-input
                        placeholder="Default Dose (required, minimum 1)"
                        v-model="data.dose"
                        name="dose"
                        class="col-3 mr-1"
                        v-validate="'required'"
                        :class="{
                            'border-danger': errors.has('dose')
                        }"
                        :disabled="disable.dose"
                    />
                    <b-select
                        v-model="data.uom"
                        class="col form-control"
                        :options="masterUoms"
                        name="uom"
                        :disabled="disable.uom"
                    ></b-select>
                </b-row>
            </b-form-group>

            <b-form-group
                label="Serial Number:"
                class
                label-cols="4"
                label-for="serial_no"
            >
                <b-row class="mx-0">
                    <b-input
                        placeholder="Serial Number (optional)"
                        v-model="data.serial_no"
                        name="serial_no"
                        :class="{
                            'border-danger': errors.has('serial_no')
                        }"
                    />
                </b-row>
            </b-form-group>

            <b-form-group label="Note:" class label-cols="4" label-for="note">
                <b-form-textarea
                    placeholder="Note (optional)"
                    v-model="data.note"
                    rows="3"
                    name="note"
                />
            </b-form-group>

            <template v-slot:modal-footer="{ cancel, close }">
                <modal-footer
                    :is-loading="isLoading"
                    ok-title="Save"
                    @ok-action="save(close)"
                    @cancel-action="cancel()"
                />
            </template>
        </b-modal>
    </div>
</template>
<script>
import vSelect from "vue-select";
import { find, difference, sortBy, forEach } from "lodash";
import ItemsServices from "@/services/items";

export default {
    components: {
        vSelect,
        ModalFooter: () => import("@/components/elements/modal-footer")
    },
    mixins: [],
    data() {
        return {
            loading: false,
            setting: {},
            data: {
                start: moment(),
                letter_id: null,
                due: 0,
                control: 0,
                category: null,
                fees_id: null,
                is_print_on_invoice: 1,
                cost_type: 1,
                uom: 1,
                dose: 1,
                min_price: 0,
                price: 0,
                print_label: 0,
                note: null,
                serial_no: null
            },
            disable: {
                controlled: false,
                fees: false,
                dose: false,
                uom: false,
                due: false,
                minPrice: false
            },
            show: {
                basePrice: true // price_dose in apps // TODO: check if still relevance ?
            },
            templates: [],
            message: "",
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            isLoading: false
        };
    },
    watch: {
        data: {
            immediate: true,
            handler(newValue, oldValue) {
                if (newValue.category) {
                    if (
                        oldValue.category &&
                        oldValue.category.item_type !==
                            newValue.category.item_type
                    ) {
                        this.checkItemType();
                    } else {
                        this.checkItemType();
                    }
                }
            },
            deep: true
        }
    },
    computed: {
        groupedCategories() {
            return this.$store.getters["inventory/groupedCategories"];
        },
        dueOptions() {
            return this.$store.getters["inventory/dueOptions"];
        },
        masterUoms() {
            let uoms = [];
            forEach(this.$store.getters["references/uoms"], function (uom) {
                uoms.push({
                    value: uom.value,
                    text: uom.text,
                    labelLowercase: uom.text.toLowerCase() // for sorting only
                });
            });
            return sortBy(uoms, "labelLowercase");
        },
        fees() {
            return difference(
                this.$store.state.inventory.fees,
                this.data.fees_id
            );
        }
    },
    mounted() {},
    methods: {
        /**
         * @method checkItemType
         * @description Enable and disable fields based on Item Category Type.
         *              Some fields are no longer applicable :
         *              - price tiers
         *              - base price / price dose
         *              - discount
         *              - start_balance
         *              - instruction
         *              - location
         *              - route
         *              If these fields are to be added in this modal, need to refer to
         *              itemEdit component inside computed
         * @see apps/assets/js/module/inventory/item.js - check_item_type()
         */
        checkItemType() {
            let itemType =
                this.data.category && this.data.category.item_type
                    ? this.data.category.item_type.id
                    : null;

            if (itemType) {
                switch (Number(itemType)) {
                    case 11: // other fees
                        this.disable.controlled = true;
                        this.data.control = 0;
                        this.disable.fees = true;
                        this.data.fees_id = null;
                        this.disable.dose = true;
                        this.disable.uom = true;
                        this.disable.due = true;
                        this.disable.minPrice = true;

                        break;

                    default:
                        /**
                         *  checking for category outpatient service
                         */
                        if (Number(itemType) === 5) {
                            this.data.control = 0;
                        }

                        this.disable.controlled = false;
                        this.disable.fees = false;
                        this.disable.dose = false;
                        this.disable.uom = false;
                        this.disable.due = false;
                        this.disable.minPrice = false;
                }
            }
        },
        open(params) {
            this.isLoading = false;
            this.datePickerOptions.format = this.getDateFormat();
            Promise.all([
                this.$store.dispatch("inventory/getCategory"),
                this.$store.dispatch("inventory/getInventoryDueOptions"),
                this.$store.dispatch("inventory/getFees"),
                this.$store.dispatch("references/getUOM", { module: 1 })
            ]).then(() => {
                this.data.next_due = 0;
                this.data.category = find(
                    this.groupedCategories,
                    category => !category.unselectable
                );
            });
            this.$refs["itemModal"].show();
            if (params.name) {
                this.data.name = params.name;
            }
        },
        async save(close) {
            if (await this.$validator.validateAll()) {
                let params = {
                    control: this.data.control,
                    cost_type_id: this.data.cost_type,
                    discount_amount: "0",
                    discount_type: "0",
                    dose: this.data.dose,
                    due: this.data.due,
                    fees: this.data.fees_id,
                    is_print_on_invoice: this.data.is_print_on_invoice,
                    item_type_category_id: this.data.category.id,
                    minimum_price: this.data.min_price,
                    module: "patient",
                    name: this.data.name,
                    price: this.data.price,
                    reminder_start: "",
                    type: this.data.category.item_type.id,
                    uom: this.data.uom,
                    print_label: this.data.print_label,
                    note: this.data.note,
                    serial_no: this.data.serial_no
                };

                let dueRef = this.dueOptions.find(d => d.value === params.due);
                let dueSeconds = dueRef ? dueRef.seconds : 0;
                this.isLoading = true;

                return ItemsServices.post(params).then(item => {
                    this.isLoading = false;
                    this._message("Item added successfully", "success");
                    close();
                    this.$emit("saved", {
                        ...item,
                        label: item.name,
                        uom: item.uom.label ?? "Unit(s)",
                        location: 0,
                        route: 0,
                        item_type: item.itemType.id,
                        item_type_category_id: item.item_type_category.id,
                        dueSeconds: dueSeconds,
                        isNewCreated: true // to be used in New Medical Record dialog
                    });
                });
            }
        }
    }
};
</script>
<style lang="scss">
#category {
    .vs__dropdown-option--disabled {
        &:hover {
            background: white;
        }
        color: black;
        font-weight: bold;
        margin-left: 0 !important;
    }
    .vs__dropdown-option {
        margin-left: 0.3rem;
    }
}
</style>
